import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Link, withRouter } from 'react-router-dom'
import { Alert, Card, Col, Empty, Row, Space, Table, Tag, Typography } from 'antd'
import PageTitle from 'components/PageTitle'
import { getRequest, growthReviewUrl } from '../../config'
import { numbersRows, stoppedAt } from './utils'

const { Paragraph, Text } = Typography

// One week's look-back: the numbers exactly as computed, then the AI's reading of them
// (spec: 0042_growth_bot Req 3.2–3.4).
const ReviewDetail = ({ match }) => {
  const [review, setReview] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getRequest(growthReviewUrl(match.params.id), 'review', setLoading).then(response => {
      if (response.success) setReview(response.data)
    })
  }, [match.params.id])

  if (!review) {
    return (
      <div className="container-fluid">
        <Card loading={loading}>{!loading ? <Empty description="Review not found" /> : null}</Card>
      </div>
    )
  }

  const numbers = review.numbers || {}
  const rows = numbersRows(numbers.this, numbers.previous)
  const stops = stoppedAt(numbers.this)
  const text = review.text || {}
  const cited = review.cited || []
  const start = moment(review.weekStart)

  return (
    <div className="container-fluid">
      <PageTitle title="Growth · Weekly review" />
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title={`Week of ${start.format('D MMM')} – ${start.clone().add(6, 'days').format('D MMM YYYY')}`}>
          {!review.ok ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="The review couldn't be written"
              description={review.error || 'No reason was recorded.'}
            />
          ) : null}
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={rows}
            columns={[
              { title: 'Number', dataIndex: 'label' },
              { title: 'This week', dataIndex: 'now' },
              { title: 'Week before', dataIndex: 'before' },
              { title: 'Change', dataIndex: 'change' }
            ]}
            scroll={{ x: 500 }}
          />
          {stops.length ? (
            <Paragraph style={{ marginTop: 16 }}>
              <Text strong>Stopped at: </Text>
              {stops.map(([where, n]) => `${where} (${n})`).join(', ')}
            </Paragraph>
          ) : null}
        </Card>

        {text.quietWeek ? <Alert type="info" showIcon message="A quiet week: too few chats to read much into." /> : null}

        <Row gutter={[16, 16]}>
          {text.wentWell && text.wentWell.length ? (
            <Col xs={24} lg={8}>
              <Card title="Went well" size="small">
                {text.wentWell.map(line => (
                  <Paragraph key={line}>• {line}</Paragraph>
                ))}
              </Card>
            </Col>
          ) : null}
          {(text.problems || []).map((p, i) => (
            <Col xs={24} lg={8} key={p.title}>
              <Card title={`${i + 1}. ${p.title}`} size="small">
                {p.evidence ? <Paragraph type="secondary">{p.evidence}</Paragraph> : null}
                {p.fix ? (
                  <Paragraph>
                    <Text strong>Fix: </Text>
                    {p.fix}
                  </Paragraph>
                ) : null}
              </Card>
            </Col>
          ))}
        </Row>

        {cited.length ? (
          <Card title="Chats behind these findings" size="small">
            <Space wrap>
              {cited.map((id, i) => (
                <Link key={id} to={`/duty-assistant/conversations/${id}`}>
                  <Tag>Chat {i + 1}</Tag>
                </Link>
              ))}
            </Space>
          </Card>
        ) : null}
      </Space>
    </div>
  )
}

ReviewDetail.propTypes = { match: PropTypes.object }

export default withRouter(ReviewDetail)
